<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { fetchJobs, claimJob, type Job } from '../game/quests-api';
import { toast } from '../game/toast';
import { BG, COMBAT_SCENE, tierBadge } from '../game/art';
import { t } from '../i18n';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import nerveIcon from '../assets/fimbulwake/icons/nerve.webp';

const emit = defineEmits<{ (e: 'updated'): void }>();

const loading = ref(true);
const error = ref('');
const busy = ref<string | null>(null);
const collectingAll = ref(false);
const jobs = ref<Job[]>([]);
/** Job id currently playing its "COLLECTED" stamp moment. */
const stamped = ref<string | null>(null);

const daily = computed(() => jobs.value.filter((j) => j.type === 'DAILY'));
const weekly = computed(() => jobs.value.filter((j) => j.type === 'WEEKLY'));
const claimables = computed(() => jobs.value.filter((j) => j.claimable));
const doneToday = computed(() => daily.value.filter((j) => j.claimable || j.status === 'COMPLETED').length);

/** Summed payout waiting across every claimable job (for the Collect-all pitch). */
const waiting = computed(() => claimables.value.reduce(
  (acc, j) => ({
    cash: acc.cash + j.reward.cash,
    bars: acc.bars + j.reward.goldBars,
    xp: acc.xp + j.reward.xp,
  }),
  { cash: 0, bars: 0, xp: 0 },
));

// ── Reset clocks — mirrors the backend cron truth (core/cron/cron.service.ts):
//    dailies reset 00:00 UTC, weeklies Monday 00:00 UTC. Display only.
const now = ref(Date.now());
let tick: number | undefined;
onMounted(() => { tick = window.setInterval(() => { now.value = Date.now(); }, 1000); });
onUnmounted(() => { if (tick) window.clearInterval(tick); });

function nextUtcMidnight(): number {
  const d = new Date(now.value);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
}
function nextUtcMonday(): number {
  const d = new Date(now.value);
  const days = (8 - d.getUTCDay()) % 7 || 7; // days until next Monday
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days);
}
function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const hh = String(h).padStart(2, '0'), mm = String(m).padStart(2, '0'), ss = String(sec).padStart(2, '0');
  return d > 0 ? `${d}d ${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`;
}
const dailyClock = computed(() => clock(nextUtcMidnight() - now.value));
const weeklyClock = computed(() => clock(nextUtcMonday() - now.value));

// ── Per-action dossier art: a small scene/icon medallion + an accent color.
// EVERY seeded action (jobs.constants.ts JOB_ACTIONS) must resolve to an image —
// an unmapped action used to render as an empty circle (QA: "Blood in the Pit"
// had no entry here at all, since 'pvp_win' was missing from this table).
const ACTION_LOOK: Record<string, { img?: string; accent: string }> = {
  enemies_beaten: { img: COMBAT_SCENE.strike, accent: '#c0504d' },
  cash_earned:    { img: cashIcon,            accent: '#c8a24c' },
  tiles_moved:    { img: COMBAT_SCENE.flee,   accent: '#5b9bd5' },
  pvp_win:        { img: nerveIcon,           accent: '#8f2323' }, // brass knuckles — the Pit
  explored:       { img: COMBAT_SCENE.guard,  accent: '#5fc2ad' },
  market_bought:  { img: BG.market,           accent: '#c96a9e' },
  chat_sent:      { img: BG.office,           accent: '#9a86ab' },
  turf_taken:     { img: tierBadge(),         accent: '#d98e32' },
};
// Fallback for any job action NOT in the table above (a future job def added
// without updating this file) — a generic tier badge beats a blank circle.
const DEFAULT_LOOK = { img: tierBadge(), accent: 'var(--color-gold)' };
const look = (j: Job) => ACTION_LOOK[j.action] ?? DEFAULT_LOOK;

function pct(j: Job): number {
  if (j.target <= 0) return 0;
  return Math.min(100, Math.round((Math.min(j.progress, j.target) / j.target) * 100));
}
/** Small targets read better as pips; big tallies as a filled track. */
const usePips = (j: Job) => j.target <= 12;

/** A job already paid out this reset period — COMPLETED but no longer
 *  claimable. Presentational only: derived from the same status/claimable
 *  fields the template already reads, so the day-book can score the entry
 *  through instead of drawing it as either "still working" or "ready". */
function isSettled(j: Job): boolean {
  return j.status === 'COMPLETED' && !j.claimable;
}

async function refresh() {
  error.value = '';
  try { jobs.value = (await fetchJobs()).jobs; }
  catch (e) { error.value = (e as Error).message; }
}
onMounted(async () => { await refresh(); loading.value = false; });

async function onClaim(j: Job) {
  if (!j.claimable || busy.value) return;
  busy.value = j.id;
  error.value = '';
  try {
    const res = await claimJob(j.id);
    const parts: string[] = [];
    if (res.reward.soft) parts.push(t('jobs.toast.silver', { amount: res.reward.soft.toLocaleString() }));
    if (res.reward.premium) parts.push(t('jobs.toast.runes', { amount: res.reward.premium.toLocaleString() }));
    if (res.reward.xp) parts.push(t('jobs.toast.xp', { amount: res.reward.xp.toLocaleString() }));
    toast(t('jobs.toast.payday', { name: j.name, details: parts.join(' · ') }), { icon: cashIcon });
    stamped.value = j.id;                         // let the COLLECTED stamp land…
    await new Promise((r) => setTimeout(r, 650)); // …before the card refreshes
    await refresh();
    emit('updated');
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    busy.value = null;
    stamped.value = null;
  }
}

async function onCollectAll() {
  if (collectingAll.value || claimables.value.length === 0) return;
  collectingAll.value = true;
  error.value = '';
  try {
    const total = { soft: 0, premium: 0, xp: 0 };
    for (const j of claimables.value) {
      try {
        const res = await claimJob(j.id);
        total.soft += res.reward.soft; total.premium += res.reward.premium; total.xp += res.reward.xp;
      } catch { /* keep collecting the rest */ }
    }
    const parts: string[] = [];
    if (total.soft) parts.push(t('jobs.toast.silver', { amount: total.soft.toLocaleString() }));
    if (total.premium) parts.push(t('jobs.toast.runes', { amount: total.premium.toLocaleString() }));
    if (total.xp) parts.push(t('jobs.toast.xp', { amount: total.xp.toLocaleString() }));
    if (parts.length) toast(t('jobs.toast.allCollected', { details: parts.join(' · ') }), { icon: cashIcon });
    await refresh();
    emit('updated');
  } finally {
    collectingAll.value = false;
  }
}
</script>

<template>
  <section class="jbd-board" :aria-label="t('jobs.boardAria')">
    <!-- THE BEAM — carved title, hanging reset clocks, day-book tally, collect-all -->
    <div class="jbd-beam">
      <h1 class="gilt-text jbd-title">{{ t('jobs.title') }}</h1>

      <div class="jbd-tags">
        <span class="jbd-tag" v-html="t('jobs.newSlate', { time: `<b class='jbd-tag-time tabular-nums'>${dailyClock}</b>` })"></span>
        <span class="jbd-tag" v-html="t('jobs.bigScores', { time: `<b class='jbd-tag-time tabular-nums'>${weeklyClock}</b>` })"></span>
        <span class="jbd-tag">{{ t('jobs.doneToday', { done: doneToday, total: daily.length }) }}</span>
      </div>

      <div class="jbd-beam-spacer"></div>

      <button
        v-if="claimables.length >= 2"
        class="btn-forge btn-forge--rune btn-forge--sm whitespace-normal text-center leading-snug"
        :disabled="collectingAll"
        @click="onCollectAll"
      >
        {{ collectingAll ? t('jobs.collecting') : t('jobs.collectAll', { count: claimables.length }) }}
        <span v-if="waiting.cash" class="ml-1">· {{ waiting.cash.toLocaleString() }} {{ t('jobs.silver') }}</span>
        <span v-if="waiting.bars" class="ml-1">· {{ waiting.bars }} {{ t('jobs.runes') }}</span>
      </button>
    </div>

    <p v-if="loading" class="jbd-note">{{ t('jobs.loading') }}</p>
    <p v-if="error" class="jbd-note jbd-note--error">{{ error }}</p>

    <div v-if="!loading" class="jbd-sections">
      <div
        v-for="grp in [
          { key: 'daily', title: t('jobs.daily.title'), blurb: t('jobs.daily.blurb'), list: daily },
          { key: 'weekly', title: t('jobs.weekly.title'), blurb: t('jobs.weekly.blurb'), list: weekly },
        ]"
        :key="grp.key"
        class="jbd-section"
      >
        <div class="jbd-section-head">
          <h2 class="jbd-section-title">{{ grp.title }}</h2>
          <p class="jbd-section-blurb">{{ grp.blurb }}</p>
        </div>

        <p v-if="grp.list.length === 0" class="jbd-empty">
          {{ t('jobs.empty') }}
        </p>

        <!-- the board: each job is its own hung tablet, not a ruled row -->
        <div v-else class="jbd-grid" :class="grp.key === 'weekly' ? 'jbd-grid--weekly' : 'jbd-grid--daily'">
          <article
            v-for="j in grp.list" :key="j.id"
            class="jbd-slab"
            :class="[
              j.claimable ? 'jbd-slab--ready' : (isSettled(j) ? 'jbd-slab--settled' : 'jbd-slab--active'),
              { 'jbd-slab--stamped': stamped === j.id },
            ]"
            :style="{ '--jb-accent': look(j).accent }"
          >
            <span class="jbd-nail" aria-hidden="true"></span>

            <!-- WAX SEAL — claimable, or the collected flip -->
            <span v-if="j.claimable && stamped !== j.id" class="jbd-seal jbd-seal--ready" aria-hidden="true">ᛗ</span>
            <span v-if="stamped === j.id" class="jbd-seal jbd-seal--paid" aria-hidden="true">✓</span>

            <div class="jbd-slab-head">
              <h3 class="jbd-name">{{ j.name }}</h3>
              <div class="jbd-medal">
                <img v-if="look(j).img" :src="look(j).img" alt="" />
              </div>
            </div>

            <p class="jbd-desc">{{ j.description }}</p>

            <!-- progress: carved notches for short tallies, a groove for big ones —
                 dropped once settled, since the struck-through name already says "done". -->
            <div v-if="!isSettled(j)" class="jbd-progress">
              <div v-if="usePips(j)" class="jbd-pips">
                <i
                  v-for="n in j.target" :key="n"
                  class="jbd-notch"
                  :class="{ 'jbd-notch-lit': n <= Math.min(j.progress, j.target) }"
                ></i>
                <span class="jbd-count">{{ Math.min(j.progress, j.target) }}/{{ j.target }}</span>
              </div>
              <div v-else>
                <div class="jbd-groove">
                  <div class="jbd-groove-fill" :style="{ width: pct(j) + '%' }"></div>
                </div>
                <div class="jbd-count">
                  {{ Math.min(j.progress, j.target).toLocaleString() }} / {{ j.target.toLocaleString() }}
                </div>
              </div>
            </div>

            <div class="jbd-foot">
              <div class="jbd-rewards">
                <span v-if="j.reward.cash" class="jbd-reward"><img :src="cashIcon" :alt="t('jobs.silver')" class="w-4 h-4 object-contain shrink-0" />{{ j.reward.cash.toLocaleString() }}</span>
                <span v-if="j.reward.goldBars" class="jbd-reward"><img :src="runesIcon" :alt="t('jobs.runes')" class="w-4 h-4 object-contain shrink-0" /><b>{{ j.reward.goldBars }}</b></span>
                <span v-if="j.reward.xp" class="jbd-reward jbd-reward-xp">᛭ {{ j.reward.xp }} {{ t('jobs.xp') }}</span>
              </div>
              <button
                v-if="j.claimable"
                :disabled="busy === j.id"
                @click="onClaim(j)"
                class="btn-forge btn-forge--rune btn-forge--sm"
              >{{ busy === j.id ? '…' : t('jobs.collect') }}</button>
              <span v-else-if="isSettled(j)" class="jbd-status jbd-status--settled">{{ t('jobs.settled') }}</span>
              <span
                v-else
                class="jbd-status"
                :title="t('jobs.errandTooltip')"
              ><i class="jbd-status-dot" aria-hidden="true"></i>{{ t('jobs.onErrand') }}</span>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* THE JOB BOARD. A timber notice-board: one big panel with a top beam, and
   below it every job pinned up as its own tablet — not a ruled list. */
.jbd-board {
  position: relative;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  box-shadow: var(--rim-ice), 0 14px 38px rgba(0, 0, 0, 0.55);
  padding: 0 22px 20px;
}

/* ── THE BEAM — a raised timber crossbar carrying the title, the two reset
   clocks and the day-book tally as hanging iron tags, and the collect-all
   plaque pinned at its right. ─────────────────────────────────────────── */
.jbd-beam {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  background: var(--surface-beam);
  background-size: var(--surface-beam-size);
  background-blend-mode: var(--surface-beam-blend);
  box-shadow: 0 3px 0 rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(226, 236, 248, 0.10);
  padding: 14px 18px;
  margin: 0 -22px 18px;
}
.jbd-title {
  font-family: var(--font-heading);
  font-size: clamp(22px, 2.6vw, 28px);
  line-height: 1.1;
  margin: 0;
  flex: none;
}
.jbd-beam-spacer { flex: 1 1 auto; min-width: 8px; }

.jbd-tags { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.jbd-tag {
  clip-path: var(--clip-hewn);
  background: var(--mat-iron);
  padding: 5px 11px;
  font-family: var(--font-label);
  font-size: 10px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #cfdae8;
  white-space: nowrap;
}
.jbd-tag-time { color: var(--color-gold-bright); font-variant-numeric: tabular-nums; }

/* Loading / error lines: their own dark plate rather than bare timber, so the
   read stays clear of the board's grain regardless of how light a given plank
   photo runs — the same guarantee the board otherwise gets from a tablet
   face or an iron tag. */
.jbd-note {
  display: inline-block;
  margin: 0 0 16px;
  padding: 8px 14px;
  background: rgba(6, 9, 14, 0.72);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.6);
  font-family: var(--font-body);
  font-style: italic;
  font-size: 13px;
  color: #9db0c6;
}
.jbd-note--error { color: var(--color-error); }

.jbd-sections { display: flex; flex-direction: column; gap: 30px; }

/* Section header — a small carved plank strip, sunk just enough to guarantee
   its own contrast floor independent of the board's grain underneath it. */
.jbd-section-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  clip-path: var(--clip-hewn);
  background: rgba(4, 6, 10, 0.6);
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.55), inset 0 0 0 1px rgba(0, 0, 0, 0.4);
  padding: 8px 16px;
  margin-bottom: 14px;
}
.jbd-section-title { font-family: var(--font-heading); font-size: 16px; color: var(--color-gold); margin: 0; }
.jbd-section-blurb { font-family: var(--font-body); font-style: italic; font-size: 11.5px; color: #9db0c6; margin: 0; }

.jbd-empty {
  display: inline-block;
  padding: 8px 14px;
  background: rgba(4, 6, 10, 0.55);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.5);
  font-family: var(--font-body);
  font-style: italic;
  font-size: 13px;
  color: #9db0c6;
}

/* ── THE TABLETS ────────────────────────────────────────────────────────── */
.jbd-grid { display: grid; gap: 18px 14px; }
.jbd-grid--daily { grid-template-columns: repeat(auto-fill, minmax(235px, 1fr)); }
.jbd-grid--weekly { grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }

.jbd-slab {
  position: relative;
  isolation: isolate;
  padding: 20px 16px 14px;
  text-align: left;
  transition: transform 200ms ease;
}
/* the tablet FACE lives on ::before, clipped to the stele shape — the root
   itself stays an unclipped rectangle so the nail and the wax seal can hang
   outside the stone's own silhouette. */
.jbd-slab::before {
  content: '';
  position: absolute; inset: 0; z-index: -1;
  clip-path: var(--clip-stele);
  background: var(--tex-stone), var(--sunken);
  background-size: 150px 150px, auto;
  background-blend-mode: soft-light, normal;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.8), inset 0 0 0 1px rgba(0, 0, 0, 0.45);
}
.jbd-slab:nth-child(odd) { transform: rotate(-0.7deg); }
.jbd-slab:nth-child(even) { transform: rotate(0.55deg); }
.jbd-slab:hover { transform: rotate(0deg) translateY(-2px); }

/* CLAIMABLE — an ember edge-glow, the one warm thing on the board. */
.jbd-slab--ready { animation: jbd-ready-glow 2.6s ease-in-out infinite; }
@keyframes jbd-ready-glow {
  0%, 100% { box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--color-ember) 20%, transparent); }
  50%      { box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--color-ember) 45%, transparent), 0 0 16px rgba(217, 135, 60, 0.16); }
}
/* the COLLECTED moment — the ember ring flips to a settled success tint while
   the seal underneath plays its slam. */
.jbd-slab--stamped {
  animation: none;
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--color-success) 45%, transparent);
}

/* SETTLED — the whole tablet grays; the name gets a carved strike-through. */
.jbd-slab--settled { filter: grayscale(0.5) brightness(0.85); }
.jbd-slab--settled .jbd-name {
  text-decoration: line-through;
  text-decoration-color: rgba(157, 176, 198, 0.65);
  text-decoration-thickness: 1.5px;
}

/* the NAIL — a pinhead at top-center, with a faint cast shadow falling onto
   the stone below it. */
.jbd-nail {
  position: absolute; top: -5px; left: 50%; transform: translateX(-50%);
  z-index: 2;
  width: 9px; height: 9px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #6a7686, #232b36 70%);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
}
.jbd-nail::after {
  content: '';
  position: absolute; top: 8px; left: 50%; transform: translateX(-50%);
  width: 1px; height: 16px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.55), transparent);
}

.jbd-slab-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; padding-right: 38px; }
.jbd-name {
  font-family: var(--font-heading);
  font-size: 17px; line-height: 1.25;
  color: #e4ebf2;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85);
  flex: 1 1 auto; min-width: 0;
}
.jbd-slab--ready .jbd-name { text-shadow: 0 0 14px color-mix(in srgb, var(--color-ember) 45%, transparent), 0 -1px 0 rgba(0, 0, 0, 0.85); }

/* the MEDALLION — a hewn coin, not a soft circle: the accent border traces
   the clipped facet edge instead of a radius. */
.jbd-medal {
  flex: none;
  width: 40px; height: 40px;
  clip-path: var(--clip-hewn);
  border: 2px solid color-mix(in srgb, var(--jb-accent, var(--color-ice-dim)) 55%, transparent);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.4);
  background: var(--color-surface-2);
  overflow: hidden;
}
.jbd-medal img { width: 100%; height: 100%; object-fit: cover; display: block; }
.jbd-slab--settled .jbd-medal { filter: grayscale(0.5) brightness(0.8); }

.jbd-desc {
  font-family: var(--font-body); font-style: italic; font-size: 11.5px;
  color: #9db0c6; line-height: 1.4; margin: 4px 0 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}

.jbd-progress { margin-top: 10px; }
.jbd-pips { display: flex; align-items: center; gap: 5px; }
.jbd-count {
  font-family: var(--font-label); font-size: 10px; letter-spacing: 0.05em;
  color: #9db0c6; font-variant-numeric: tabular-nums; margin-left: 2px;
}

/* progress as carved tally strokes — thin slanted notches, lit ones filled
   and glowing aurora. */
.jbd-notch {
  width: 3px; height: 11px;
  transform: skewX(-12deg);
  border-radius: 1px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid color-mix(in srgb, var(--color-aurora) 45%, transparent);
  transition: background 220ms ease, box-shadow 220ms ease;
}
.jbd-notch-lit {
  background: linear-gradient(180deg, var(--color-aurora), var(--color-aurora-cool));
  box-shadow: 0 0 6px color-mix(in srgb, var(--color-aurora) 55%, transparent);
  border-color: transparent;
}

/* progress as a plain carved groove, not a pill meter. */
.jbd-groove {
  height: 7px;
  background: rgba(0, 0, 0, 0.6);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.8);
  margin-bottom: 4px;
}
.jbd-groove-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--color-aurora-cool), var(--color-aurora));
  transition: width 600ms cubic-bezier(0.3, 0.8, 0.3, 1);
}

/* the WAX SEAL — claimable, or the collected flip. A genuine circle: the one
   exemption to the board's hewn-edge rule, because a seal IS round. */
.jbd-seal {
  position: absolute; top: 10px; right: 10px; z-index: 2;
  width: 34px; height: 34px; border-radius: 50%;
  display: grid; place-items: center;
  font-family: var(--font-heading); font-size: 15px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.7), inset 0 -2px 4px rgba(0, 0, 0, 0.5);
}
.jbd-seal--ready {
  background: radial-gradient(circle at 38% 32%, #e89a55, #96341f 62%, #6e2416);
  color: #ffdfc2;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.6);
}
.jbd-seal--paid {
  background: radial-gradient(circle at 38% 32%, color-mix(in srgb, var(--color-success) 65%, white), var(--color-success) 60%, color-mix(in srgb, var(--color-success) 70%, black));
  color: #eafff2;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.5);
  animation: jbd-seal-slam 300ms cubic-bezier(0.2, 1.6, 0.4, 1) both;
}
@keyframes jbd-seal-slam {
  0% { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.jbd-foot {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(207, 232, 255, 0.09);
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;
}
.jbd-rewards { display: flex; flex-wrap: wrap; gap: 6px; }
.jbd-reward {
  display: inline-flex; align-items: center; gap: 5px;
  font-family: var(--font-heading); font-size: 13px; color: var(--color-gold);
  font-variant-numeric: tabular-nums;
}
.jbd-reward-xp { color: var(--color-purple-light); }

/* jbd-status — a passive status reading, not a button. */
.jbd-status {
  display: inline-flex; align-items: center; gap: 7px;
  font-family: var(--font-label); font-size: 10px; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase;
  color: #9db0c6;
  cursor: default; user-select: none;
}
.jbd-status--settled { gap: 0; }
.jbd-status-dot {
  width: 6px; height: 6px; border-radius: 50%; flex: none;
  background: color-mix(in srgb, var(--jb-accent, var(--color-aurora)) 70%, #9db0c6);
  animation: jbd-status-pulse 1.8s ease-in-out infinite;
}
@keyframes jbd-status-pulse {
  0%, 100% { opacity: 0.4; box-shadow: 0 0 0 0 color-mix(in srgb, var(--jb-accent, var(--color-aurora)) 40%, transparent); }
  50% { opacity: 1; box-shadow: 0 0 5px 1.5px color-mix(in srgb, var(--jb-accent, var(--color-aurora)) 45%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .jbd-slab--ready { animation: none; }
  .jbd-seal--paid { animation: none; }
  .jbd-status-dot { animation: none; opacity: 0.85; }
  .jbd-slab { transition: none; }
}
</style>
